import {AbstractApiRequestInterceptor} from 'core/abstract/AbstractApiRequestInterceptor';
import {AbstractApiResponseInterceptor} from 'core/abstract/AbstractApiResponseInterceptor';
import {WrongEnvException} from 'core/exceptions/WrongEnvException';
import {isBrowserContext} from 'core/helpers';
import {
    IHttpClientResponse,
    IRequestOptions,
    TAbstractCall,
    TDecorator,
    TEnv,
    TJson,
    TRequestParameters,
} from 'core/types';
import {NotFoundControllerException} from 'core/exceptions/NotFoundControllerException';
import {STATUS_CODES} from 'core/constants';

export type TContext = 'server' | 'client';
export function contextDecorator(context: TContext): Function {
    return function (_: unknown, property: string, descriptor: PropertyDescriptor) {
        const newDescriptor = Object.assign({}, descriptor);

        newDescriptor.value = function (): unknown {
            if ('server' === context && isBrowserContext()) {
                throw new Error(`Method "${property}" not allowed on the client side!`);
            }

            if ('client' === context && !isBrowserContext()) {
                throw new Error(`Method "${property}" not allowed on the server side!`);
            }

            return descriptor.value.apply(this, arguments);
        };

        return newDescriptor;
    };
}

export function reqInterceptor(
    cbList: AbstractApiRequestInterceptor[],
    requestOption: IRequestOptions<TJson>,
): TDecorator {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    return function (_: unknown, __: string, descriptor: PropertyDescriptor) {
        const clonedDescriptor = Object.assign({}, descriptor);

        clonedDescriptor.value = async function (...args: unknown[]): Promise<IHttpClientResponse> {
            let interceptedArgs = <TRequestParameters>args[0];

            for (const interceptor of cbList) {
                interceptedArgs = interceptor.intercept(
                    <TRequestParameters<unknown, null>>interceptedArgs,
                    requestOption,
                );
            }

            return await descriptor.value.call(this, interceptedArgs);
        };

        return clonedDescriptor;
    };
}

export function resIntercept(cbList: AbstractApiResponseInterceptor[]): TDecorator {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    return function (_: unknown, __: string, descriptor: PropertyDescriptor) {
        const clonedDescriptor = Object.assign({}, descriptor);

        clonedDescriptor.value = async function (...args: unknown[]): Promise<IHttpClientResponse> {
            const response: IHttpClientResponse = await descriptor.value.apply(this, args);

            let interceptedResponse = response;
            for (const interceptor of cbList) {
                // eslint-disable-next-line no-await-in-loop
                interceptedResponse = await interceptor.intercept(interceptedResponse, <RequestInit>args[0]);
            }

            return interceptedResponse;
        };

        return clonedDescriptor;
    };
}

export function exception(): TDecorator {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    return function (_: unknown, __: string, descriptor: PropertyDescriptor) {
        const clonedDescriptor = Object.assign({}, descriptor);

        clonedDescriptor.value = async function (...args: unknown[]): Promise<IHttpClientResponse> {
            const response: IHttpClientResponse = await descriptor.value.apply(this, args);
            if (STATUS_CODES.NOT_FOUND === response.status) {
                throw new NotFoundControllerException('Not found');
            }

            return response;
        };

        return clonedDescriptor;
    };
}

export const env = <A extends TAbstractCall>(envId: TEnv, call: A): A => {
    return <A>(<unknown>function () {
        if ('server' === envId && isBrowserContext()) {
            throw new WrongEnvException(call.name || '<anonymouse>', 'client');
        }

        if ('client' === envId && !isBrowserContext()) {
            throw new WrongEnvException(call.name || '<anonymouse>', 'server');
        }

        return call(...arguments);
    });
};

export const memoize = <A extends TAbstractCall>(call: A): A => {
    const table = new Map();
    return <A>(<unknown>function () {
        // eslint-disable-next-line prefer-rest-params
        const args = arguments;

        if (table.has(args)) {
            return table.get(args);
        }

        const result = call(...args);
        table.set(JSON.stringify(args), result);

        return result;
    });
};
